/**
 * «Создать договор» во вкладке «Договор» у сделки без договора (право `contract_edit`, **пишет в боевую
 * CRM**). Сценарий владельца: клиент говорит «забронируйте это платье» → сотрудник отмечает товары (из
 * сердечек или по серии — `ProductPicker`) → «Создать договор». ФИО и телефон подставлены из карточки
 * сделки и активного номера чата, даты проката — из сделки.
 *
 * Шаги по очереди: договор (`createContract`, даты сервер берёт из сделки) → даты формы, если отличаются
 * от записанных (`patchContract`) → товары (`addPickedItems`). На ошибке шага — стоп и понятный текст:
 * договор уже создан и показан после перечитывания. Ход и итог живут у вкладки (`CreateReport`), а не
 * здесь: как только договор появится, форма сменится карточкой договора, а отчёт останется над ней.
 */
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { fmtPhone } from "@/components/chat/Contacts";
import { ProductPicker } from "@/components/contract/ProductPicker";
import { Field, inputCls } from "@/components/contract/ui";
import { DatePick } from "@/components/DateTimePick";
import { ApiFailure, type ApiErrorItem } from "@/lib/api";
import {
  type PickedItem,
  type RawContract,
  addPickedItems,
  contractQuery,
  createContract,
  isUnknownOutcome,
  patchContract,
  phoneKey,
  pickedLabel,
  refreshContract,
  writeError,
} from "@/lib/contract";
import { type DealCard, loadDealCard } from "@/lib/dealCard";
import { btn } from "@/lib/panelBtn";
import { plural } from "@/lib/plural";

/** Ход и итог создания — у вкладки «Договор» (переживает смену формы карточкой договора). */
export type CreateReport = {
  dealId: number;
  /** Идёт шаг: «Создаю договор…» и т. п.; `null` — закончили. */
  step: string | null;
  /** Номер созданного договора (и при частичном успехе). */
  dogovorId: number | null;
  /** Итог без ошибки. */
  done: string | null;
  error: string | null;
  warnings: string[];
  /** Выбранное, что не попало в договор: его ждёт «Добавить в договор» созданного договора
   * (номер не узнали — единственного договора сделки). */
  rest: PickedItem[];
};

const NAME_MAX = 128;
/** Имя чата WhatsApp — это номер; в ФИО такое не подставляем. */
const looksLikePhone = (s: string) => /^[\d\s()+\-]+$/.test(s.trim());
const d10 = (s: unknown) => (typeof s === "string" ? s.slice(0, 10) : "");
const goods = (n: number) => `${n} ${plural(n, "товар", "товара", "товаров")}`;

/** ФИО: клиент салона CRM по номеру → контакт Bitrix по номеру → любой из карточки → имя чата. */
function prefillName(card: DealCard | undefined, phone: string, chatName: string): string {
  const key = phoneKey(phone);
  const rows = card?.contacts ?? [];
  const mine = rows.filter((r) => phoneKey(r.phone) === key);
  for (const list of [mine, rows]) {
    const customer = list.flatMap((r) => r.customer).find((c) => c.name?.trim());
    if (customer?.name) return customer.name.trim();
    const contact = list.flatMap((r) => r.bitrix).find((c) => c.name?.trim());
    if (contact) return contact.name.trim();
  }
  return chatName && !looksLikePhone(chatName) ? chatName.trim() : "";
}

/** Ход и итог создания — над формой, а после — над карточкой договора. */
export function CreateStatus({ r, onClose }: { r: CreateReport; onClose?: () => void }) {
  return (
    <div className="flex flex-col gap-1 text-[12.5px]" data-annot="создание договора — итог">
      {r.step && <div className="text-fg-muted">{r.step}</div>}
      {r.done && <div className="text-ok">{r.done}</div>}
      {r.error && <div className="whitespace-pre-wrap text-err">{r.error}</div>}
      {r.warnings.length > 0 && (
        <ul className="flex flex-col text-[12px] font-medium text-err">
          {r.warnings.map((w, i) => (
            <li key={i}>{w}</li>
          ))}
        </ul>
      )}
      {onClose && !r.step && (
        <button type="button" onClick={onClose} className="self-start text-[12px] text-accent-text hover:underline">
          скрыть
        </button>
      )}
    </div>
  );
}

type Init = { name: string; phone: string; start: string; end: string };

function Form({
  dealId,
  chatId,
  init,
  report,
  onReport,
  onClose,
}: {
  dealId: number;
  chatId: number | null;
  init: Init;
  report: CreateReport | null;
  onReport: (r: CreateReport) => void;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const [name, setName] = useState(init.name);
  const [phone, setPhone] = useState(init.phone);
  const [start, setStart] = useState(init.start);
  const [end, setEnd] = useState(init.end);
  const [items, setItems] = useState<PickedItem[]>([]);
  const [fieldErr, setFieldErr] = useState<Record<string, string>>({});
  const busy = report?.step != null;

  const problems: Record<string, string> = {};
  if (!name.trim()) problems.name = "Укажите ФИО клиента";
  const pk = phoneKey(phone);
  if (pk.length < 11 || pk.length > 15) problems.phone = "Телефон — 11 цифр";
  if (start && end && end < start) problems.end = "Конец раньше начала";
  // Ошибка сервера по полю — до правки поля; своя проверка — сразу (объясняет, почему кнопка неактивна).
  const err = (k: string) => fieldErr[k] ?? problems[k];

  const run = async () => {
    setFieldErr({});
    const warnings: string[] = [];
    let id: number | null = null;
    const say = (p: Partial<CreateReport>) =>
      onReport({ dealId, step: null, dogovorId: id, done: null, error: null, warnings: [...warnings], rest: [], ...p });
    say({ step: "Создаю договор в CRM…" });
    // Неожиданный сбой (не ответ сервера) — снять «идёт шаг», чтобы форма не застряла, и показать договор.
    try {
      await steps();
    } catch (e) {
      say({ error: `${id ? `Договор ${id} создан, но дальше сбой` : "Сбой"}: ${e instanceof Error ? e.message : String(e)}`, rest: items });
      if (id !== null) refreshContract(qc, dealId, id, chatId);
    }

    async function steps() {
      // Остановились до дат и товаров — что доделать в договоре.
      const toAdd = "товары ждут в «Добавить в договор» — нажмите «Добавить»";
      const unfinished = `\nДаты проката сверьте в договоре${items.length ? `, ${toAdd}` : ""}.`;

      // 1. Договор. Даты, салон и контакт сервер берёт из сделки.
      let created: RawContract | undefined;
      try {
        const r = await createContract(dealId, { phone: phone.trim(), name: name.trim() }, chatId);
        id = r.dogovor_id;
        created = r.contract;
        warnings.push(...r.warnings);
      } catch (e) {
        if (e instanceof ApiFailure && e.code === "CONTRACT_CREATED_PARTIAL") {
          id = Number((e.items[0] as ApiErrorItem & { dogovor_id?: number }).dogovor_id) || null;
          say({ error: `${e.message}.${unfinished}`, rest: items });
          refreshContract(qc, dealId, id, chatId);
          return;
        }
        // Выбранное не теряем: появится единственный договор сделки — ждёт в его «Добавить в договор».
        const kept = items.length ? ` Выбранные товары ждут в «Добавить в договор» — нажмите «Добавить».` : "";
        if (e instanceof ApiFailure && e.code === "CONTRACT_EXISTS") {
          say({ error: `У сделки уже есть договор — показываю его.${kept}`, rest: items });
          refreshContract(qc, dealId, null, chatId);
          return;
        }
        if (e instanceof ApiFailure && e.code === "CONTRACT_BUSY") {
          // Договор сделки создаёт параллельный запрос — перечитать: появится, покажем его.
          say({ error: `Договор не создан: ${e.message}`, rest: items });
          refreshContract(qc, dealId, null, chatId);
          return;
        }
        if (isUnknownOutcome(e)) {
          // Сеть или шлюз (502/504): CRM могла создать договор — не повторяем, а перечитываем сделку.
          say({ error: `Результат неизвестен (${writeError(e)}) — перечитываю договор сделки. Появится — проверьте его.${kept}`, rest: items });
          refreshContract(qc, dealId, null, chatId);
          return;
        }
        if (e instanceof ApiFailure && e.status === 422) {
          const byField: Record<string, string> = {};
          for (const it of e.items) if (it.field) byField[it.field === "customerPhone" ? "phone" : it.field] = it.message;
          setFieldErr(byField);
        }
        say({ error: `Договор не создан: ${writeError(e)}` });
        return;
      }

      const made = `Договор ${id} создан`;
      let contract: RawContract;
      try {
        contract = created ?? (await qc.fetchQuery(contractQuery(id)));
      } catch (e) {
        say({ error: `${made}, но не перечитался: ${e instanceof Error ? e.message : String(e)}.${unfinished}`, rest: items });
        refreshContract(qc, dealId, id, chatId);
        return;
      }
      let version = contract.version;

      // 2. Даты формы, если отличаются от взятых из сделки. Пустое поле — не стираем даты сделки.
      const dates: Record<string, string> = {};
      if (start && start !== d10(contract.dogovor.rentStart)) dates.rentStart = start;
      if (end && end !== d10(contract.dogovor.rentEnd)) dates.rentEnd = end;
      let datesNote = "";
      if (Object.keys(dates).length > 0) {
        say({ step: `${made}. Ставлю даты проката…` });
        const stopped = items.length ? `\nТовары не добавлены — ${toAdd}.` : "";
        let written = false;
        try {
          const r = await patchContract(id, dates, version, chatId);
          written = true;
          warnings.push(...r.warnings);
          version = r.contract?.version ?? (await qc.fetchQuery(contractQuery(id))).version;
          datesNote = ", даты записаны";
        } catch (e) {
          const why = written && e instanceof Error ? e.message : writeError(e);
          say({
            error: written
              ? `${made}, даты записаны, но договор не перечитался: ${why}${stopped}`
              : `${made}, даты проката не записаны: ${why}${stopped}`,
            rest: items,
          });
          refreshContract(qc, dealId, id, chatId);
          return;
        }
      }

      // 3. Товары по очереди, каждый — со свежей версией договора.
      if (items.length > 0) {
        const a = await addPickedItems(id, items, version, chatId, qc, (done, total) =>
          say({ step: `${made}${datesNote}. Добавляю товары ${done + 1} из ${total}…` }),
        );
        warnings.push(...a.warnings);
        if (a.error !== null) {
          const why = writeError(a.error);
          const rest = `\nОставшиеся ${toAdd}.`;
          say({
            error: a.reading
              ? `${made}${datesNote}, добавлено ${a.done} из ${items.length}, дальше не идём: договор не перечитался — ${why}${rest}`
              : `${made}${datesNote}, добавлено ${a.done} из ${items.length}. «${pickedLabel(items[a.done]!)}» не добавлен: ${why}${rest}`,
            rest: items.slice(a.done),
          });
          refreshContract(qc, dealId, id, chatId);
          return;
        }
      }
      say({ done: `✓ ${made}${datesNote}${items.length ? `, ${goods(items.length)} в договоре` : ""}.` });
      refreshContract(qc, dealId, id, chatId);
    }
  };

  const ready = Object.keys(problems).length === 0 && !busy;
  return (
    <div className="flex flex-col gap-2 rounded-[var(--radius-ui)] border border-border bg-bg p-2 text-[12.5px]" data-annot="создать договор">
      <div className="font-medium">Новый договор</div>
      <div className="flex flex-wrap gap-2">
        <Field label="ФИО клиента" error={err("name")}>
          <input
            className={`${inputCls} ${err("name") ? "border-err" : "border-border"}`}
            value={name}
            maxLength={NAME_MAX}
            disabled={busy}
            onChange={(e) => (setName(e.target.value), setFieldErr(({ name: _, ...rest }) => rest))}
          />
        </Field>
        <Field label="Телефон" error={err("phone")}>
          <input
            className={`${inputCls} tabular-nums ${err("phone") ? "border-err" : "border-border"}`}
            value={phone}
            inputMode="tel"
            disabled={busy}
            onChange={(e) => (setPhone(e.target.value), setFieldErr(({ phone: _, ...rest }) => rest))}
          />
        </Field>
      </div>
      <div className="flex flex-wrap gap-2">
        <Field label="Начало проката" error={err("rentStart")}>
          <DatePick
            label="Начало проката"
            value={start}
            disabled={busy}
            placeholder="не задано"
            onChange={(d) => (setStart(d), end && end < d && setEnd(d))}
          />
        </Field>
        <Field label="Конец проката" error={err("end") ?? err("rentEnd")}>
          <DatePick
            label="Конец проката"
            value={end}
            disabled={busy}
            placeholder="не задан"
            align="right"
            onChange={(d) => (setEnd(d), start && start > d && setStart(d))}
          />
        </Field>
      </div>
      <div className="font-medium">Товары</div>
      <ProductPicker chatId={chatId} dealId={dealId} rentStart={start || null} rentEnd={end || null} exclude={[]} disabled={busy} onChange={setItems} />
      {report && <CreateStatus r={report} />}
      <div className="flex flex-wrap items-center gap-2">
        <button type="button" className={btn.primary} disabled={!ready} onClick={() => void run()}>
          {busy ? "Записываю в CRM…" : items.length > 0 ? `Создать договор (${goods(items.length)})` : "Создать договор"}
        </button>
        <button type="button" className={btn.quiet} disabled={busy} onClick={onClose}>
          Отмена
        </button>
      </div>
      <div className="text-[11.5px] text-fg-muted">Договор переведёт сделку в «Забронировано».</div>
    </div>
  );
}

export function CreateContract({
  dealId,
  chatId,
  phone,
  name = "",
  report,
  onReport,
}: {
  dealId: number;
  chatId: number | null;
  /** Активный номер чата — первым в «Телефон»; нет — первый номер сделки. */
  phone?: string | null | undefined;
  /** Имя чата — ФИО, если в карточке сделки клиента не нашлось. */
  name?: string | undefined;
  report: CreateReport | null;
  onReport: (r: CreateReport) => void;
}) {
  const [open, setOpen] = useState(false);
  // Та же карточка, что в панели сделки (кэш): ФИО, номера и даты проката.
  const card = useQuery({ queryKey: ["deal-card", dealId], queryFn: () => loadDealCard(dealId), staleTime: 60_000, enabled: open });
  if (!open)
    return (
      <div className="flex flex-col gap-1.5 text-[12.5px]">
        <div className="text-fg-muted">Договора нет.</div>
        {report && <CreateStatus r={report} />}
        <button type="button" className={`${btn.outline} self-start`} onClick={() => setOpen(true)} data-annot="кнопка создать договор">
          Создать договор
        </button>
        <div className="text-[11.5px] text-fg-muted">Договор переведёт сделку в «Забронировано».</div>
      </div>
    );
  if (card.isPending) return <div className="text-[12.5px] text-fg-muted">Загружаю сделку…</div>;
  // Карточка не загрузилась — форма всё равно открывается, поля пустые (их можно заполнить).
  const tel = phone || card.data?.phones[0] || "";
  const init: Init = {
    name: prefillName(card.data, tel, name),
    phone: tel ? fmtPhone(phoneKey(tel)) : "",
    start: d10(card.data?.deal.rent_start),
    end: d10(card.data?.deal.rent_end),
  };
  return (
    <div className="flex flex-col gap-1.5">
      {card.isError && <div className="text-[12px] text-warn">Карточка сделки не загрузилась ({card.error.message}) — заполните поля сами.</div>}
      <Form dealId={dealId} chatId={chatId} init={init} report={report} onReport={onReport} onClose={() => setOpen(false)} />
    </div>
  );
}
